import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin } from 'rxjs';

import {
  VolunteerService,
  PastStudentHistoryItem,
  PastVolunteerHistoryItem,
  StatusAuditItem,
} from '../../services/volunteer.service';
import { FolioPillComponent } from '../ui/folio-pill/folio-pill.component';
import { PaginationComponent } from '../ui/pagination/pagination.component';

type HistoryTab = 'students' | 'volunteers' | 'audit';

/**
 * Vista de Historial de voluntarios (Fase D.1).
 *
 * Tres pestañas:
 *   - Estudiantes pasados (muestra horas acreditadas)
 *   - Voluntarios pasados (SIN columna de horas)
 *   - Bitácora (una fila por transición de estado)
 *
 * Los datos se cargan en ngOnInit vía los 3 métodos de VolunteerService
 * (getPastStudents / getPastVolunteers / getStatusAudit).
 */
@Component({
  selector: 'app-volunteers-history',
  standalone: true,
  imports: [CommonModule, FolioPillComponent, PaginationComponent],
  templateUrl: './volunteers-history.component.html',
  styleUrls: ['./volunteers-history.component.scss'],
})
export class VolunteersHistoryComponent implements OnInit {
  private readonly volunteerService = inject(VolunteerService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  activeTab: HistoryTab = 'students';

  pastStudents: PastStudentHistoryItem[] = [];
  pastVolunteers: PastVolunteerHistoryItem[] = [];
  statusAudit: StatusAuditItem[] = [];

  // PAGINACIÓN INDEPENDIENTE POR PESTAÑA
  studentsPage = 1;
  volunteersPage = 1;
  auditPage = 1;
  pageSize = 8;

  get pagedPastStudents(): PastStudentHistoryItem[] {
    const start = (this.studentsPage - 1) * this.pageSize;
    return this.pastStudents.slice(start, start + this.pageSize);
  }

  get pagedPastVolunteers(): PastVolunteerHistoryItem[] {
    const start = (this.volunteersPage - 1) * this.pageSize;
    return this.pastVolunteers.slice(start, start + this.pageSize);
  }

  get pagedStatusAudit(): StatusAuditItem[] {
    const start = (this.auditPage - 1) * this.pageSize;
    return this.statusAudit.slice(start, start + this.pageSize);
  }

  isLoading = true;
  loadError = false;

  ngOnInit(): void {
    forkJoin({
      students: this.volunteerService.getPastStudents(),
      volunteers: this.volunteerService.getPastVolunteers(),
      audit: this.volunteerService.getStatusAudit(),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ students, volunteers, audit }) => {
          this.pastStudents = students ?? [];
          this.pastVolunteers = volunteers ?? [];
          this.statusAudit = audit ?? [];
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error: (err: unknown) => {
          console.error('❌ Error cargando historial de voluntarios:', err);
          this.isLoading = false;
          this.loadError = true;
          this.cdr.detectChanges();
        },
      });
  }

  setTab(tab: HistoryTab): void {
    this.activeTab = tab;
  }
}
